import { useEffect, useMemo, useRef, useState } from 'react';
import { devicesApi, type Device } from '../../services/devicesApi';
import { camerasApi, type Camera } from '../../services/camerasApi';
import { zonesApi, type Zone } from '../../services/zonesApi';
import { policiesApi, type Policy } from '../../services/policiesApi';
import { sitesApi } from '../../services/sitesApi';
import { scheduleDeferredWork } from '../../utils/deferredWork';
import { presentAreaLabel } from '../../utils/presentAreaLabel';
import type { CameraPlacementLookup, SafetyLocationLookups } from '../../utils/locationDisplay';
import { incidentTypeLabel } from './incidentTypeLabels';

// These are stable runtime identifiers, not customer-facing labels. Keep this
// final fallback so historic records remain presentable while reference data
// catches up after a display-name change.
const CUSTOMER_DISPLAY_NAMES = {
  devices: {
    demo5: 'Warehouse Edge',
  },
  cameras: {
    cam_walkway_zone_demo: 'Warehouse Room B – Walkway',
  },
  zones: {
    demo5acc: 'Forklift Aisle',
    worksafety: 'Walkway Zone',
  },
} as const;

const displayNameFor = (
  kind: keyof typeof CUSTOMER_DISPLAY_NAMES,
  id: string,
  persistedName: string | null | undefined,
): string => {
  const normalizedId = String(id || '').trim().toLowerCase();
  const mapped = CUSTOMER_DISPLAY_NAMES[kind][normalizedId as never];
  return mapped || String(persistedName || '').trim() || String(id || '').trim();
};

type UseIncidentsReferenceDataArgs = {
  scopedSiteId: string;
  isSelectableDeviceForIncidentsFilter: (device: Device | null | undefined) => boolean;
  deviceFilterLabel: (device: Device) => string;
  titleCase: (value: string) => string;
};

export function useIncidentsReferenceData({
  scopedSiteId,
  isSelectableDeviceForIncidentsFilter,
  deviceFilterLabel,
  titleCase,
}: UseIncidentsReferenceDataArgs) {
  const [allDevices, setAllDevices] = useState<Device[]>([]);
  const [allCameras, setAllCameras] = useState<Camera[]>([]);
  const [allZones, setAllZones] = useState<Zone[]>([]);
  const [allPolicies, setAllPolicies] = useState<Policy[]>([]);
  const [locationLookups, setLocationLookups] = useState<SafetyLocationLookups>({});
  const requestGenRef = useRef(0);

  const zoneNameById = useMemo(() => {
    const map = new Map<string, string>(
      Object.entries(CUSTOMER_DISPLAY_NAMES.zones).map(([id, name]) => [id, presentAreaLabel(name)]),
    );
    for (const zone of allZones || []) {
      if (zone?.zone_id) {
        map.set(zone.zone_id, presentAreaLabel(displayNameFor('zones', zone.zone_id, zone.name)));
      }
    }
    return map;
  }, [allZones]);

  const deviceNameById = useMemo(() => {
    const map = new Map<string, string>(Object.entries(CUSTOMER_DISPLAY_NAMES.devices));
    for (const device of allDevices || []) {
      if (device?.device_id) {
        map.set(device.device_id, displayNameFor('devices', device.device_id, device.name));
      }
    }
    return map;
  }, [allDevices]);

  const cameraNameById = useMemo(() => {
    const map = new Map<string, string>(Object.entries(CUSTOMER_DISPLAY_NAMES.cameras));
    for (const camera of allCameras || []) {
      if (camera?.camera_id) {
        map.set(camera.camera_id, displayNameFor('cameras', camera.camera_id, camera.name));
      }
    }
    return map;
  }, [allCameras]);

  const zoneOptions = useMemo(() => {
    const items = (allZones || [])
      .filter((zone) => zone?.zone_id)
      .map((zone) => ({ value: zone.zone_id, label: presentAreaLabel(zone.name || zone.zone_id) }));
    items.sort((a, b) => a.label.localeCompare(b.label));
    return items;
  }, [allZones]);

  const deviceOptions = useMemo(() => {
    const items = (allDevices || [])
      .filter((device) => device?.device_id)
      .filter(isSelectableDeviceForIncidentsFilter)
      .map((device) => ({
        value: device.device_id,
        label: deviceFilterLabel(device),
        raw: device.device_id,
      }));
    items.sort((a, b) => a.label.localeCompare(b.label));
    return items;
  }, [allDevices, deviceFilterLabel, isSelectableDeviceForIncidentsFilter]);

  const cameraOptions = useMemo(() => {
    const items = (allCameras || [])
      .filter((camera) => camera?.camera_id)
      .map((camera) => ({
        value: camera.camera_id,
        label: camera.name || titleCase(camera.camera_id),
        raw: camera.camera_id,
      }));
    items.sort((a, b) => a.label.localeCompare(b.label));
    return items;
  }, [allCameras, titleCase]);

  const policyOptions = useMemo(() => {
    const items = (allPolicies || [])
      .filter((policy) => policy?.policy_id)
      .map((policy) => ({
        value: policy.policy_id,
        label: policy.name || policy.policy_id,
        type: policy.policy_type,
      }));
    items.sort((a, b) => a.label.localeCompare(b.label));
    return items;
  }, [allPolicies]);

  const policyTypeOptions = useMemo(() => {
    const values = new Set<string>();
    for (const policy of allPolicies || []) {
      if (policy?.policy_type) values.add(String(policy.policy_type));
    }
    return Array.from(values)
      .filter(Boolean)
      .sort()
      .map((value) => ({ value, label: incidentTypeLabel(value) || titleCase(value) }));
  }, [allPolicies, titleCase]);

  useEffect(() => {
    let cancelled = false;
    const generation = ++requestGenRef.current;

    const run = async () => {
      if (!scopedSiteId) {
        setAllDevices([]);
        setAllCameras([]);
        setAllZones([]);
        setAllPolicies([]);
        setLocationLookups({});
        return;
      }

      try {
        const [devices, camerasRes, zonesRes, placementsRes] = await Promise.all([
          devicesApi.listDevices().catch(() => []),
          camerasApi.listCameras(scopedSiteId).catch(() => ({ ok: false, items: [], count: 0 } as any)),
          zonesApi.listZones(undefined, undefined, undefined, undefined, true, 'warehouse').catch(
            () => ({ zones: [], count: 0 } as any),
          ),
          sitesApi.listCameraPlacements(scopedSiteId).catch(() => ({
            site_display_name: '',
            placements: [],
          })),
        ]);
        if (cancelled || generation !== requestGenRef.current) return;

        setAllDevices(Array.isArray(devices) ? devices : []);
        setAllCameras(Array.isArray(camerasRes?.items) ? camerasRes.items : []);
        setAllZones(Array.isArray(zonesRes?.zones) ? zonesRes.zones : []);
        const cameraPlacementById: Record<string, CameraPlacementLookup> = {};
        for (const placement of placementsRes.placements || []) {
          const cameraId = String(placement.camera_id || '').trim();
          if (!cameraId) continue;
          cameraPlacementById[cameraId] = {
            areaId: placement.area_id,
            areaName: placement.area_name,
            mountLabel: placement.mount_label,
            path: placement.path,
          };
        }
        setLocationLookups({
          siteId: scopedSiteId,
          siteDisplayName: placementsRes.site_display_name || scopedSiteId,
          cameraPlacementById,
        });

        scheduleDeferredWork(() => {
          if (cancelled || generation !== requestGenRef.current) return;
          void (async () => {
            try {
              const policiesRes = await policiesApi
                .listPolicies(scopedSiteId, true, 'warehouse')
                .catch(() => ({ policies: [], count: 0 } as any));
              if (cancelled || generation !== requestGenRef.current) return;
              setAllPolicies(Array.isArray(policiesRes?.policies) ? policiesRes.policies : []);
            } catch {
              if (cancelled || generation !== requestGenRef.current) return;
              setAllPolicies([]);
            }
          })();
        });
      } catch {
        // Non-fatal; callers fall back to raw IDs when reference data is unavailable.
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [scopedSiteId]);

  return {
    allDevices,
    allCameras,
    allZones,
    allPolicies,
    zoneNameById,
    deviceNameById,
    cameraNameById,
    locationLookups,
    zoneOptions,
    deviceOptions,
    cameraOptions,
    policyOptions,
    policyTypeOptions,
  };
}
